import React, { FC } from 'react'
import { useSelector } from 'react-redux'
import Table from 'uiKit/table/Table'
import TableRow from 'uiKit/table/TableRow'
import TableCell from 'uiKit/table/TableCell'
import { ResendInviteIcon } from 'uiKit/icons/ResendInviteIcon'
import { UserType } from '../../model/usersModel'
import { InputEditor } from '../InputEditor/InputEditor'
import {
  ResendInviteButton,
  UserEmail,
  UserEmailContainer,
  UserStatus,
  UserStatusRow,
} from './ProfessionalsTable.styles'
import { UserStatuses } from '../../helpers/constants'
import { RoleSelector } from '../RoleSelector/RoleSelector'
import { ActionSelector } from '../ActionSelector/ActionSelector'
import { ExportConversations } from '../ExportConversations/ExportConversations'
import { SupervisionStatus } from '../SupervisionStatus/SupervisionStatus'
import { DuplicateWarningMessage } from '../../../serviceUsers/components/ServiceUsersTable/ServiceUsersTable.styles'
import { isQcsBot } from 'constants/qcs'
import { getUserRole, ROLE } from 'security'

const CONVERSATIONS_COLUMN = { title: 'Export conversations logs', maxWidth: 205 }

const buildColumnTitles = (showConversationLogs: boolean, showAction: boolean) => [
  { title: 'First Name', maxWidth: 160 },
  { title: 'Last Name', maxWidth: 160 },
  { title: 'Email', maxWidth: 215 },
  // { title: 'Resend Invite', maxWidth: 400 },
  {
    title: 'Manager',
    maxWidth: 180,
    tooltipText: 'Assigned Managers will receive notifications about Supervisions and Knowledge Checks related to selected Care Processional.'
  },
  { title: 'Supervision status', maxWidth: 100 },
  ...(showConversationLogs ? [CONVERSATIONS_COLUMN] : []),
  ...(showAction ? [{ title: 'Action', maxWidth: 50 }] : []),
]

const getRowBackground = (index: number) => {
  return { background: index % 2 === 0 ? '' : 'var(--color-table-row-primary)' }
}

interface Props {
  users: UserType[]
  botId: string
  refreshUsers: () => void
  openDeleteModalHandler: (user: UserType) => void
  openSupervisionManagementModalHandler: (user: UserType) => void
  openResendInviteModalHandler: (user: UserType) => void
}

export const ProfessionalsTable: FC<Props> = ({
  users,
  botId,
  refreshUsers,
  openDeleteModalHandler,
  openSupervisionManagementModalHandler,
  openResendInviteModalHandler
}) => {
  const activeBot = useSelector((state: any) => state.activeBot)
  const adminUser = useSelector((state: any) => state.adminUser)
  const isQcs = isQcsBot(activeBot || botId)
  const isCareManager = getUserRole(botId) === ROLE.MANAGER
  const showConversationLogs = !isQcs
  // Action column stays visible for Care Managers; own-row actions are gated below.
  const showAction = true
  const columnTitles = buildColumnTitles(showConversationLogs, showAction)

  const isOwnEntry = (user: UserType) => {
    const adminEmail = adminUser?.email
    const userEmail = user?.email
    if (!adminEmail || !userEmail) return false
    return String(adminEmail).toLowerCase() === String(userEmail).toLowerCase()
  }

  const canResendInvite = (status: string) =>
    status === UserStatuses.EXPIRED || status === UserStatuses.SENT

  const userStatusHandler = (status) => {
    let statusStr = ''
    switch (status) {
      case UserStatuses.EXPIRED:
        statusStr = 'Invitation link expired'
        break
      case UserStatuses.SENT:
        statusStr = 'Pending invitation'
        break
    }

    return statusStr
  }

  return (
    <Table titles={columnTitles}>
        {users.map((user, index) => {
          const ownEntry = isOwnEntry(user)
          const rowDisabled = isQcs && user.archived === true
          // Care Manager: edit/actions only locked on their own professional row.
          // Admin/Careco on QCS: names stay read-only for everyone. Archived rows too.
          const namesReadOnly = rowDisabled || (isCareManager ? ownEntry : isQcs)
          const lockManagerSelect = (isCareManager && ownEntry) || rowDisabled
          const showRowAction = !(isCareManager && ownEntry) && !rowDisabled

          return (
          <TableRow
            key={user.id}
            style={{
              alignItems: user.is_duplicate ? 'center' : 'center',
              height: user.is_duplicate ? '124px' : 'inherit',
              marginBottom:  user.is_duplicate ? '0' : 'auto',
              marginTop:  user.is_duplicate ? '0' : 'auto',
              paddingTop: user.is_duplicate ? '0' : 'auto',
              opacity: rowDisabled ? 0.55 : undefined,
              pointerEvents: rowDisabled ? 'none' : undefined,
              ...getRowBackground(index),
            }}>
            <TableCell style={{ maxWidth: columnTitles[1].maxWidth + 18, flexDirection: 'column', position: 'relative' }}>
              <InputEditor
                user={user}
                value={user.first_name}
                userKey="first_name"
                botId={botId}
                refreshUsers={refreshUsers}
                readOnly={namesReadOnly}
              />
              {
                user.is_duplicate &&
                <DuplicateWarningMessage style={{ position: 'absolute', top: '24px', left: 0, width: '222px' }}>
                  This name combination already exists. Delete duplicate or add indicator (e.g. middle name) to
                  differentiate professional.
                </DuplicateWarningMessage>
              }
            </TableCell>
            <TableCell style={{ maxWidth: columnTitles[1].maxWidth }}>
              <InputEditor
                user={user}
                value={user.last_name}
                userKey="last_name"
                botId={botId}
                refreshUsers={refreshUsers}
                readOnly={namesReadOnly}
              />
            </TableCell>
            <TableCell style={{ maxWidth: columnTitles[2].maxWidth }}>
              <UserEmailContainer>
                <UserEmail>{user.email}</UserEmail>
                    {user.link_status && (
                      <UserStatusRow>
                        <UserStatus status={user.link_status}>{userStatusHandler(user.link_status)}</UserStatus>
                        {!isQcs && canResendInvite(user.link_status) && (
                          <ResendInviteButton onClick={() => openResendInviteModalHandler(user)}>
                            Resend invite
                            <ResendInviteIcon />
                          </ResendInviteButton>
                        )}
                      </UserStatusRow>
                    )}
              </UserEmailContainer>
            </TableCell>
            <TableCell style={{ maxWidth: columnTitles[3].maxWidth }}>
              <RoleSelector
                manager={user.manager}
                user={user}
                botId={botId}
                refreshUsers={refreshUsers}
                isDisabled={lockManagerSelect}
              />
            </TableCell>
            <TableCell style={{ maxWidth: columnTitles[4].maxWidth }}>
              <SupervisionStatus type={user.supervision_status} />
            </TableCell>
            {showConversationLogs && (
              <TableCell style={{ maxWidth: CONVERSATIONS_COLUMN.maxWidth }}>
                <ExportConversations botId={botId} userId={user.id} />
              </TableCell>
            )}
            {showAction && (
              <TableCell style={{ maxWidth: 50 }}>
                {showRowAction && (
                  <ActionSelector
                    user={user}
                    refreshUsers={refreshUsers}
                    openDeleteModalHandler={openDeleteModalHandler}
                    openSupervisionManagementModalHandler={openSupervisionManagementModalHandler}
                    isQcs={isQcs}
                  />
                )}
              </TableCell>
            )}
          </TableRow>
          )
        })}
    </Table>
  )
}
